import { type FormEvent, useState } from "react";
import { getUsersApi, inviteUser } from "@praesto/core";
import { Modal, Button, Input, Select, FormField } from "@praesto/ui-kit";
import { ROLE_LABEL, ROLE_ORDER } from "./roleLabels";
import { DepartmentMultiSelect } from "./DepartmentMultiSelect";

export function InviteUserModal(props: {
  hotels: ReadonlyArray<{ id: string; name: string }>;
  onClose: () => void;
  onDone: () => void;
}) {
  const [email, setEmail] = useState("");
  const [role, setRole] = useState<
    "tenant_owner" | "manager_hotel" | "manager_functional" | "staff"
  >("staff");
  const [hotelId, setHotelId] = useState(props.hotels[0]?.id ?? "");
  const [departments, setDepartments] = useState<ReadonlyArray<string>>([]);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function submit(e: FormEvent) {
    e.preventDefault();
    setBusy(true);
    setError(null);
    try {
      const api = getUsersApi();
      const tok = await api.getAuthToken();
      await inviteUser(
        api.apiBase,
        {
          email,
          role,
          hotelId,
          departments: [...departments],
        },
        tok,
      );
      props.onDone();
    } catch (e2) {
      setError(String(e2));
    } finally {
      setBusy(false);
    }
  }

  return (
    <Modal open onClose={props.onClose} title="Invita via email">
      <form onSubmit={submit} className="space-y-3">
        <FormField label="Email" required>
          <Input
            type="email"
            inputMode="email"
            autoCapitalize="none"
            autoCorrect="off"
            value={email}
            required
            onChange={(e) => setEmail(e.target.value)}
          />
        </FormField>
        <FormField label="Ruolo">
          <Select value={role} onChange={(e) => setRole(e.target.value as typeof role)}>
            {ROLE_ORDER.map((r) => (
              <option key={r} value={r}>
                {ROLE_LABEL[r]}
              </option>
            ))}
          </Select>
        </FormField>
        <FormField label="Hotel" required>
          <Select
            value={hotelId}
            required
            onChange={(e) => {
              setHotelId(e.target.value);
              setDepartments([]); // codes are hotel-specific — reset on hotel change
            }}
          >
            {props.hotels.map((h) => (
              <option key={h.id} value={h.id}>
                {h.name}
              </option>
            ))}
          </Select>
        </FormField>
        <FormField label="Reparti">
          <DepartmentMultiSelect hotelId={hotelId} value={departments} onChange={setDepartments} />
        </FormField>
        <div className="flex flex-col-reverse gap-2 sm:flex-row sm:justify-end">
          <Button
            type="button"
            variant="ghost"
            className="w-full sm:w-auto"
            onClick={props.onClose}
          >
            Annulla
          </Button>
          <Button
            type="submit"
            variant="primary"
            className="w-full sm:w-auto"
            loading={busy}
            disabled={busy || !email || !hotelId}
          >
            Invia invito
          </Button>
        </div>
        {error && <p className="text-sm text-danger">{error}</p>}
      </form>
    </Modal>
  );
}
